import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { AlertCircle, Loader2 } from "lucide-react"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { useTranslations } from "next-intl"




interface InfluencerDialogProps {
  open: boolean
  mode: "create" | "add-code"
  onOpenChange: (open: boolean) => void
  onSubmit: () => void
  errors: {
    general?: string
    code?: string
    name?: string
  }
  codeValue: string
  onCodeChange: (value: string) => void
  nameValue: string
  onNameChange: (value: string) => void
  isSubmitting: boolean
  targetInfluencerName?: string
}

export const InfluencerDialog = ({
  open,
  mode,
  onOpenChange,
  onSubmit,
  errors,
  codeValue,
  onCodeChange,
  nameValue,
  onNameChange,
  isSubmitting,
  targetInfluencerName,
}: InfluencerDialogProps) => {


  const tUi = useTranslations("Shopify.ShopifyAttributionDashboardDialogsInfluencerDialog")
  const tCommonUi = useTranslations("Common")

  const isCreateMode = mode === "create"

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle><span key={isCreateMode ? tUi("addInfluencer") : tUi("addDiscountCode")}>{isCreateMode ? tUi("addInfluencer") : tUi("addDiscountCode")}</span></DialogTitle>
          <DialogDescription>
            <span key={isCreateMode
              ? tUi("registerANewInfluencerWithAnInitialDiscount")
              : tUi("registerAnAdditionalDiscountCodeForValue0", { value0: String(targetInfluencerName) })}>{isCreateMode
              ? tUi("registerANewInfluencerWithAnInitialDiscount")
              : tUi("registerAnAdditionalDiscountCodeForValue0", { value0: String(targetInfluencerName) })}</span>
          </DialogDescription>
        </DialogHeader>
        {errors.general && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{errors.general}</AlertDescription>
          </Alert>
        )}
        <div className="grid gap-4 py-4">
          {isCreateMode ? (
            <div className="grid gap-2">
              <Label htmlFor="influencer-name">
                <span><span key={tUi("influencerName")}>{tUi("influencerName")}</span></span> <span className="text-destructive">*</span>
              </Label>
              <Input
                id="influencer-name"
                placeholder={tUi("enterInfluencerName")}
                value={nameValue}
                onChange={(event) => onNameChange(event.target.value)}
                disabled={isSubmitting}
                className={cn(errors.name && "border-destructive")}
              />
              {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
            </div>
          ) : (
            <div className="grid gap-1">
              <Label className="text-sm text-muted-foreground"><span key={tUi("influencer")}>{tUi("influencer")}</span></Label>
              <p className="text-base font-medium">{targetInfluencerName}</p>
            </div>
          )}
          <div className="grid gap-2">
            <Label htmlFor="code">
              <span><span key={tUi("discountCode")}>{tUi("discountCode")}</span></span> <span className="text-destructive">*</span>
            </Label>
            <Input
              id="code"
              placeholder={tUi("enterDiscountCode")}
              value={codeValue}
              onChange={(event) => onCodeChange(event.target.value)}
              disabled={isSubmitting}
              className={cn(errors.code && "border-destructive")}
            />
            {errors.code && <p className="text-sm text-destructive">{errors.code}</p>}
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
          <Button onClick={onSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                <span key={tCommonUi("saving")}>{tCommonUi("saving")}</span></>
            ) : isCreateMode ? (
              tUi("addInfluencer")
            ) : (
              tUi("addCode")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
